import { useState } from 'react'
import { Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CreditStatusBadge } from '@/components/billing/credit-status-badge'
import { InvoiceDetailModal } from '@/components/billing/invoice-detail-modal'
import { useInvoices } from '@/hooks/use-billing'
import { creditLeft, creditReason } from '@/lib/credit-notes'
import { formatKES } from '@/lib/utils'
import { ProfileStatCards } from './profile-stat-cards'
import type { Invoice } from '@riverpark/types'

const LIMIT = 100

function formatDate(unixSeconds?: number) {
  if (!unixSeconds) return '—'
  return new Date(unixSeconds * 1000).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function MemberCreditNotesTab({ memberId }: { memberId: string }) {
  const [activeInvoice, setActiveInvoice] = useState<Invoice | null>(null)
  const { data, isLoading } = useInvoices({
    memberId,
    invoiceType: 'credit_note',
    page: 1,
    limit: LIMIT,
  })
  const creditNotes = data?.data ?? []

  const totalAvailable =
    Math.round(creditNotes.reduce((sum, c) => sum + creditLeft(c), 0) * 100) / 100

  return (
    <div className="space-y-4">
      <ProfileStatCards
        stats={[
          {
            label: 'Credit on account',
            value: formatKES(totalAvailable),
            sub: totalAvailable > 0 ? 'Taken off their next bill' : 'No unused credit',
            variant: totalAvailable > 0 ? 'success' : 'neutral',
          },
        ]}
      />

      {isLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      ) : creditNotes.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
          No credit notes yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded border border-border-2">
          <table className="w-full font-sans text-sm">
            <thead>
              <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
                <th className="px-4 py-3">Credit note #</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">What it's for</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Used</th>
                <th className="px-4 py-3">Left</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {creditNotes.map((c) => (
                <tr key={c.id} className="border-b border-border-2 last:border-0">
                  <td className="px-4 py-3 font-medium text-ink">{c.invoiceNumber}</td>
                  <td className="px-4 py-3 text-ink">{formatDate(c.createdAt)}</td>
                  <td className="px-4 py-3 text-ink">{creditReason(c)}</td>
                  <td className="px-4 py-3 text-ink">{formatKES(Math.abs(c.amount))}</td>
                  <td className="px-4 py-3 text-ink">
                    {c.amountPaid > 0 ? formatKES(c.amountPaid) : '—'}
                  </td>
                  <td className="px-4 py-3 text-ink">
                    {creditLeft(c) > 0 ? formatKES(creditLeft(c)) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <CreditStatusBadge credit={c} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="secondary" size="sm" onClick={() => setActiveInvoice(c)}>
                      <Eye size={14} className="mr-1.5" />
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeInvoice && (
        <InvoiceDetailModal invoice={activeInvoice} onClose={() => setActiveInvoice(null)} />
      )}
    </div>
  )
}
